import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";
import { getCacheEntry, setCacheEntry } from "@/utils/versionedCache";

const CACHE_SCHEMA = "auth-session";
const CACHE_VERSION = 1;
const SESSION_KEY = "session";

interface CachedSession {
  session: Session | null;
  user: User | null;
  updatedAt: string;
}

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    console.log('[Auth] Initializing authentication...');

    let mounted = true;

    const cached = getCacheEntry<CachedSession>(CACHE_SCHEMA, CACHE_VERSION, SESSION_KEY);
    if (cached) {
      console.log('[Auth] Hydrating session from cache');
      setSession(cached.session);
      setUser(cached.user);
      setLoading(false);
    }
    
    // Get initial session with aggressive timeout
    const getSession = async () => {
      try {
        console.log('[Auth] Fetching session...');
        
        // Create a timeout promise
        const timeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(() => reject(new Error('Session timeout')), 5000);
        });
        
        // Race between session fetch and timeout
        const { data, error: sessionError } = await Promise.race([
          supabase.auth.getSession(),
          timeoutPromise
        ]);
        
        if (!mounted) return;
        
        if (sessionError) {
          console.error('[Auth] Session error:', sessionError);
          setError(sessionError.message);
          setSession(null);
          setUser(null);
        } else {
          console.log('[Auth] Session retrieved:', data.session ? 'authenticated' : 'guest');
          setSession(data.session);
          setUser(data.session?.user ?? null);
          setError(null);
          setCacheEntry<CachedSession>(CACHE_SCHEMA, CACHE_VERSION, SESSION_KEY, {
            session: data.session,
            user: data.session?.user ?? null,
            updatedAt: new Date().toISOString(),
          });
        }
      } catch (err) {
        if (!mounted) return;
        
        const errorMsg = err instanceof Error ? err.message : 'Auth initialization failed';
        console.warn('[Auth] Warning:', errorMsg);
        
        // Don't block the app - allow guest access
        setSession(null);
        setUser(null);
        setError(null);
      } finally {
        if (mounted) {
          console.log('[Auth] Loading complete');
          setLoading(false);
        }
      }
    };

    getSession();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!mounted) return;
        console.log('[Auth] Auth state changed:', event);
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
        setError(null);
        setCacheEntry<CachedSession>(CACHE_SCHEMA, CACHE_VERSION, SESSION_KEY, {
          session,
          user: session?.user ?? null,
          updatedAt: new Date().toISOString(),
        });
      }
    );

    return () => {
      mounted = false;
      console.log('[Auth] Cleaning up');
      subscription.unsubscribe();
    };
  }, []);

  return { user, session, loading, error };
}